import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadWwwWebFilterFile } from '@/api/generated/www-web-filters/www-web-filters';
import { WebFilterFileSlot } from '@/api/generated/schemas';
import { Button } from '@/shared/ui';
import { wwwFileUrl } from '../../internal/wwwFileUrl';

export interface AssetFileFieldProps {
  fileId: number | null;
  fileName?: string | null;
  webProjectId: number;
  slot: (typeof WebFilterFileSlot)[keyof typeof WebFilterFileSlot];
  accept: string;
  emptyLabel: string;
  uploadLabel: string;
  clearLabel?: string;
  previewClassName?: string;
  onUploaded: (fileId: number, fileName: string) => void;
  onClear: () => void;
}

/** Okamžitý upload do WebFilter slotu + náhled přes `/api/www/files/{id}`. */
export function AssetFileField({
  fileId,
  fileName,
  webProjectId,
  slot,
  accept,
  emptyLabel,
  uploadLabel,
  clearLabel,
  previewClassName = 'h-[52px] w-[86px]',
  onUploaded,
  onClear,
}: AssetFileFieldProps) {
  const { t } = useTranslation('www');
  const upload = useUploadWwwWebFilterFile();
  const [localError, setLocalError] = useState<string | null>(null);

  const canUpload = webProjectId > 0;

  return (
    <div className="gap-phx-2 flex flex-wrap items-center">
      <div
        className={`bg-phx-surface-3 border-phx-border-2 rounded-phx overflow-hidden border ${previewClassName}`}
      >
        {fileId ? (
          <img src={wwwFileUrl(fileId)} alt="" className="h-full w-full object-cover" />
        ) : null}
      </div>
      <span className="text-phx-xs text-phx-text-muted">
        {fileName ?? (fileId ? `#${fileId}` : emptyLabel)}
      </span>
      <label
        className={`bg-phx-surface-2 text-phx-xs border-phx-border-2 rounded-phx px-phx-2 py-phx-1 relative inline-flex items-center border font-bold ${
          canUpload && !upload.isPending
            ? 'hover:bg-phx-surface-3 cursor-pointer'
            : 'cursor-not-allowed opacity-60'
        }`}
      >
        {/*
          Input musí ležet přes viditelný label (ne sr-only) — jinak browser při focus
          scrollne ke skrytému 1×1 px elementu a stránka „skáče“.
        */}
        <input
          type="file"
          accept={accept}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          disabled={!canUpload || upload.isPending}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            setLocalError(null);
            try {
              const response = await upload.mutateAsync({
                data: { webProjectId, slot, file },
              });
              onUploaded(response.fileId, response.fileName);
            } catch {
              setLocalError(t('webFilter.toast.uploadError'));
            }
          }}
        />
        {upload.isPending ? t('webFilter.display.uploading') : uploadLabel}
      </label>
      {fileId ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={upload.isPending}
          onClick={() => {
            setLocalError(null);
            onClear();
          }}
        >
          {clearLabel ?? '✕'}
        </Button>
      ) : null}
      {localError ? <span className="text-phx-xs text-phx-danger w-full">{localError}</span> : null}
      {!canUpload ? (
        <span className="text-phx-xs text-phx-text-muted w-full">
          {t('webFilter.display.uploadNeedsProject')}
        </span>
      ) : null}
    </div>
  );
}
